import { createSelector } from 'reselect'
import { isBoostrappedSelector } from 'selectors/app'
import { emptyObj } from 'util/objects'
import * as UserPreferences from 'inbox/user/preferences'
import * as deviceTypes from 'constants/device_types'
import { createBasicSelector } from 'util/redux'
import { HIDE_INSTALL_APP_MENU } from 'ducks/flags/flagNames'
import { selectFlag } from 'ducks/flags'
import { selectCurrentUser } from './base'

export const selectPreferences = createSelector(
  selectCurrentUser,
  currentUser => {
    return currentUser.preferences || emptyObj
  }
)

const makeSelectBooleanPreference = (name, defaultValue = false) => state => {
  const preference = selectPreferences(state)[name]
  return preference !== undefined ? !!preference : defaultValue
}

export const selectPrefersReplyOpen = createSelector(
  selectPreferences,
  prefs => {
    return UserPreferences.repliesAsOpen(prefs)
  }
)
export const selectPrefersUnifiedInbox = makeSelectBooleanPreference(
  'unified_inbox'
)

export const selectPrefersAllMailboxesSectionVisible = makeSelectBooleanPreference(
  'prefers_v2_show_all_mailboxes_section'
)

export const selectShowAvatarsInMessages = makeSelectBooleanPreference(
  'show_avatars_in_messages'
)

export const selectShowKbSearchOnInboxEditor = makeSelectBooleanPreference(
  'show_kb_search_on_inbox_editor'
)

export const selectPrefersAgentsCanMoveToInaccessibleMailboxes = makeSelectBooleanPreference(
  'prefers_agents_can_move_to_inaccessible_mailboxes'
)

export const selectShouldUseSpeedyGroove = makeSelectBooleanPreference(
  'speedy_groove'
)

export const selectCurrentUserPrefersClassicView = makeSelectBooleanPreference(
  'classic_view'
)

export const selectCurrentUserPrefersAutoloadFirstTicketInList = makeSelectBooleanPreference(
  'autoload_first_ticket_in_list'
)

export const selectCurrentUserPrefersAutoadvanceToNextTicket = makeSelectBooleanPreference(
  'autoadvance_to_next_ticket'
)

export const selectCurrentUserPrefersOpenFolderOnTicketReply = makeSelectBooleanPreference(
  'open_folder_on_ticket_reply'
)

export const selectCurrentUserPrefersOpenFolderOnNoteAdd = makeSelectBooleanPreference(
  'open_folder_on_note_add'
)

export const selectCurrentUserPrefersOpenFolderOnTicketSave = makeSelectBooleanPreference(
  'open_folder_on_ticket_save'
)

export const selectCurrentUserPrefersAutoAdvanceOnOpen = makeSelectBooleanPreference(
  'chat_auto_advance_on_open',
  false
)

export const selectCurrentUserPrefersAutoAdvanceOnSnoozed = makeSelectBooleanPreference(
  'chat_auto_advance_on_snoozed',
  true
)

export const selectCurrentUserPrefersAutoAdvanceOnClosed = makeSelectBooleanPreference(
  'chat_auto_advance_on_close',
  true
)

export const selectCurrentUserPrefersAutoAdvanceOnSpam = makeSelectBooleanPreference(
  'chat_auto_advance_on_spam',
  true
)

export const selectCurrentUserPrefersAutoAdvanceOnTrash = makeSelectBooleanPreference(
  'chat_auto_advance_on_trash',
  true
)

export const selectCurrentUserPrefersAutoAdvanceOnAssign = makeSelectBooleanPreference(
  'chat_auto_advance_on_assign',
  true
)

export const selectCurrentUserPrefersPreviewOnHover = makeSelectBooleanPreference(
  'conversation_preview_on_hover',
  false
)

export const selectCurrentUserPrefersAutoAdvanceOnFollow = makeSelectBooleanPreference(
  'chat_auto_advance_on_follow',
  false
)

export const selectCurrentUserPrefersChatAutoAssignOnReply = makeSelectBooleanPreference(
  'chat_auto_assign_on_reply',
  false
)

export const selectCurrentUserPrefersChatAutoAssignOnNote = makeSelectBooleanPreference(
  'chat_auto_assign_on_note',
  false
)

export const selectCurrentUserPrefersInterestInChannelTwitter = makeSelectBooleanPreference(
  'interest_in_channel_twitter',
  false
)

export const selectCurrentUserPrefersInterestInChannelPhone = makeSelectBooleanPreference(
  'interest_in_channel_phone',
  false
)

export const selectCurrentUserPrefersInterestInChannelSms = makeSelectBooleanPreference(
  'interest_in_channel_sms',
  false
)

export const selectCurrentUserPrefersInterestInChannelWhatsapp = makeSelectBooleanPreference(
  'interest_in_channel_whatsapp',
  false
)

export const selectAutoAdvancePreferences = createSelector(
  selectCurrentUserPrefersAutoAdvanceOnOpen,
  selectCurrentUserPrefersAutoAdvanceOnSnoozed,
  selectCurrentUserPrefersAutoAdvanceOnClosed,
  selectCurrentUserPrefersAutoAdvanceOnSpam,
  selectCurrentUserPrefersAutoAdvanceOnTrash,
  selectCurrentUserPrefersAutoAdvanceOnAssign,
  selectCurrentUserPrefersAutoAdvanceOnFollow,
  (
    autoAdvanceOnOpen,
    autoAdvanceOnSnoozed,
    autoAdvanceOnClosed,
    autoAdvanceOnSpam,
    autoAdvanceOnTrash,
    autoAdvanceOnAssign,
    autoAdvanceOnFollow
  ) => ({
    autoAdvanceOnOpen,
    autoAdvanceOnSnoozed,
    autoAdvanceOnClosed,
    autoAdvanceOnSpam,
    autoAdvanceOnTrash,
    autoAdvanceOnAssign,
    autoAdvanceOnFollow,
  })
)

export const selectPrefersClassicView = createSelector(
  selectCurrentUserPrefersClassicView,
  state => state.app && state.app.deviceType === deviceTypes.DESKTOP,
  (classicView, isDesktop) => classicView && isDesktop
)

export const selectAutoLoadFirstTicketInList = createSelector(
  selectPrefersClassicView,
  selectCurrentUserPrefersAutoloadFirstTicketInList,
  (classicView, autoload) => !classicView && autoload
)

export const selectAutoAdvanceToNextTicketInList = createSelector(
  selectPrefersClassicView,
  selectCurrentUserPrefersAutoadvanceToNextTicket,
  (classicView, advance) => !classicView && advance
)

export const selectPrefersOpenFolderOnTicketReply = createSelector(
  selectPrefersClassicView,
  selectCurrentUserPrefersOpenFolderOnTicketReply,
  (classicView, openFolder) => {
    return classicView && openFolder
  }
)

export const selectPrefersOpenFolderOnNoteAdd = createSelector(
  selectPrefersClassicView,
  selectCurrentUserPrefersOpenFolderOnNoteAdd,
  (classicView, openFolder) => {
    return classicView && openFolder
  }
)

export const selectPrefersOpenFolderOnTicketSave = createSelector(
  selectPrefersClassicView,
  selectCurrentUserPrefersOpenFolderOnTicketSave,
  (classicView, openFolder) => {
    return classicView && openFolder
  }
)

export const selectPreferredMailboxId = createSelector(
  selectCurrentUser,
  currentUser => {
    return (currentUser || {}).preferredMailboxId || ''
  }
)

// HACK (jscheel && msaspence): Account-level timezone preference is pulled in
// through the current user, even though it is account level.
export const selectAccountTimezone = createSelector(
  selectPreferences,
  preferences => {
    const { timezone: { account: { id } = {} } = {} } = preferences || {}
    return id
  }
)

export const selectPreferredInboxTheme = createBasicSelector(
  selectPreferences,
  preferences => {
    return preferences.inbox_theme || 'light'
  }
)

export const selectPreferredPersonalUsageOnboarding = createSelector(
  selectPreferences,
  preferences => {
    return preferences.personal_usage_onboarding || emptyObj
  }
)

export const selectInstalledMobileApp = createSelector(
  selectPreferredPersonalUsageOnboarding,
  personalUsageOnboarding => {
    return (
      // Falsey values are never included, so if the key doesn't exists, it is false
      personalUsageOnboarding.ios_installed ||
      personalUsageOnboarding.android_installed
    )
  }
)

export const selectShowMobileAppsMenu = createSelector(
  isBoostrappedSelector,
  selectInstalledMobileApp,
  state => selectFlag(state)(HIDE_INSTALL_APP_MENU),
  (isBootstrapped, installedMobileApp, hideInstallAppMenu) => {
    // Falsy value for installedMobileApp may mean the data isn't fetched, so need to make sure is bootstrapped
    return isBootstrapped
      ? hideInstallAppMenu === false && !installedMobileApp
      : false
  }
)
